import type { AlertDto } from '@/api/WhisApi/generated';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { getAlertWording } from '@screens/Notifications/notifications.utils';
import { formatDateForLocale } from '@utils/date.utils';
import { Pressable } from 'react-native';
import { SizableText, XStack, YStack, useTheme } from 'tamagui';

interface NotificationListItemProps {
  alert: AlertDto;
  onPress?: () => void;
  disabled?: boolean;
}

export const NotificationListItem = ({
  alert,
  onPress,
  disabled = false,
}: NotificationListItemProps): React.ReactElement => {
  const { t, language } = useAppTranslation();
  const theme = useTheme();
  const reportCode = alert.reportCode ?? alert.reportId;
  const alertDate =
    formatDateForLocale(alert.createdAt, language, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }) ?? '';
  const alertWording = getAlertWording(t, alert.action, reportCode);

  return (
    <Pressable onPress={onPress} disabled={disabled}>
      <XStack
        alignItems="center"
        gap="$2"
        padding="$2"
        backgroundColor={alert.read ? '$background' : '$tertiary50'}
      >
        <YStack
          width={40}
          height={40}
          borderRadius="$circle"
          alignItems="center"
          justifyContent="center"
          backgroundColor={alert.read ? '$tertiary50' : '$brand'}
        >
          <Ionicons
            name="document-outline"
            size={20}
            color={alert.read ? theme.color.val : theme.background.val}
          />
        </YStack>

        <YStack flex={1} gap="$1">
          <XStack alignItems="center" justifyContent="space-between" gap="$2">
            <SizableText fontFamily="$bold">{alertWording.title}</SizableText>
            <SizableText size="$2" color="$secondary50">
              {alertDate}
            </SizableText>
          </XStack>
          <SizableText color="$secondary50">{alertWording.description}</SizableText>
        </YStack>
      </XStack>
    </Pressable>
  );
};
